/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * Controlled stack of Focus Ask fields, shared by the single-Ask modal and the
 * bulk-Ask edit modal. Owners pass `formData` + a single `updateField`
 * callback, plus a `fieldIdPrefix` so picker `fieldId`s don't collide when
 * multiple instances are mounted at once (e.g. bulk rows).
 */
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { CommentField } from '../fields/comment/CommentField';
import { EditorField } from '../fields/editor/EditorField';
import { ImpactedWorkField } from '../fields/impacted-work';
import { type ImpactedWorkResolutionStatus } from '../fields/impacted-work/useImpactedWorkResolution';
import { LinksField } from '../fields/links/LinksField';
import { NameField } from '../fields/name';
import { PriorityField } from '../fields/priority';
import { TargetDateField } from '../fields/target-date';
import { TeamPickerField } from '../fields/team';
import { UserPickerField } from '../fields/user';
import messages from '../messages';
import type { FocusAskFormData } from '../types';

const styles = cssMap({
	formBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
});

export type FocusAskFormFieldsProps = {
	formData: FocusAskFormData;
	onChange: <K extends keyof FocusAskFormData>(field: K, value: FocusAskFormData[K]) => void;
	cloudId: string;
	orgId: string;
	/**
	 * Unique prefix appended to every picker `fieldId` so multiple mounted
	 * instances (e.g. one per bulk row) don't share DOM ids.
	 */
	fieldIdPrefix: string;
	/**
	 * Notified whenever the impacted-work field's resolution status changes so
	 * parents (e.g. the create modal) can disable submission while a URL is
	 * still being resolved.
	 */
	onImpactedWorkResolutionStatusChange?: (status: ImpactedWorkResolutionStatus) => void;
};

export const FocusAskFormFields = ({
	formData,
	onChange,
	cloudId,
	orgId,
	fieldIdPrefix,
	onImpactedWorkResolutionStatusChange,
}: FocusAskFormFieldsProps): JSX.Element => {
	return (
		<div css={styles.formBody}>
			<NameField value={formData.name} onChange={(value) => onChange('name', value)} />
			<EditorField
				name="description"
				label={messages.descriptionFieldLabel}
				placeholder={messages.descriptionEditorPlaceholder}
				value={formData.description}
				onChange={(value) => onChange('description', value)}
			/>
			<EditorField
				name="justification"
				label={messages.justificationFieldLabel}
				placeholder={messages.justificationEditorPlaceholder}
				value={formData.justification}
				onChange={(value) => onChange('justification', value)}
			/>
			<TargetDateField
				value={formData.targetDate}
				onChange={(value) => onChange('targetDate', value)}
				fieldId={`${fieldIdPrefix}-targetDate`}
			/>
			<ImpactedWorkField
				value={formData.impactedWork}
				cloudId={cloudId}
				onValueChange={(value) => onChange('impactedWork', value)}
				onResolutionStatusChange={onImpactedWorkResolutionStatusChange}
			/>
			<LinksField links={formData.links} onChange={(value) => onChange('links', value)} />
			<CommentField
				value={formData.commentData}
				onChange={(value) => onChange('commentData', value)}
				fieldId={`${fieldIdPrefix}-comment`}
			/>
			<UserPickerField
				name="submitter"
				label={messages.submitterFieldLabel}
				placeholder={messages.submitterPickerPlaceholder}
				cloudId={cloudId}
				value={formData.submitter}
				onChange={(user) => onChange('submitter', user ?? null)}
				isRequired
				fieldId={`${fieldIdPrefix}-submitter`}
				isClearable={false}
			/>
			<UserPickerField
				name="owner"
				label={messages.receiverFieldLabel}
				placeholder={messages.receiverPickerPlaceholder}
				cloudId={cloudId}
				value={formData.owner}
				onChange={(user) => onChange('owner', user ?? null)}
				fieldId={`${fieldIdPrefix}-owner`}
			/>
			<TeamPickerField
				name="submittingTeam"
				label={messages.submittingTeamFieldLabel}
				placeholder={messages.submittingTeamPickerPlaceholder}
				cloudId={cloudId}
				orgId={orgId}
				fieldId={`${fieldIdPrefix}-submittingTeam`}
				value={formData.submittingTeam}
				onChange={(team) => onChange('submittingTeam', team ?? null)}
			/>
			<TeamPickerField
				name="receivingTeam"
				label={messages.receivingTeamFieldLabel}
				placeholder={messages.receivingTeamPickerPlaceholder}
				cloudId={cloudId}
				orgId={orgId}
				fieldId={`${fieldIdPrefix}-receivingTeam`}
				value={formData.receivingTeam}
				onChange={(team) => onChange('receivingTeam', team ?? null)}
			/>
			<PriorityField value={formData.priority} onChange={(value) => onChange('priority', value)} />
		</div>
	);
};
